import { ClassInstance } from "../types/class.type";
import { DateTimeUtils } from "../utils/datetime.utils";
import { ObjectUtils } from "../utils/object.utils";

/**
 * Service for managing class schedules.
 */
export class ClassScheduleService {


    /**
     * Retrieves the first class schedule by class ID from the given class data.
     * 
     * @param classInstances - The class data containing the class schedules.
     * @param classInstanceId - The ID of the class schedule to search for.
     * @returns The first class schedule matching the given class ID, or undefined if not found.
     */
    static getClassScheduleById(classInstances: ClassInstance[], classInstanceId: number): ClassInstance | undefined {
        if (classInstances) {
            return classInstances.find(schedule => schedule.id === classInstanceId);
        }
        return undefined;
    }


    /**
     * Retrieves a class schedule from the given class data based on the target time.
     * @param classInstances - The class data containing the schedules.
     * @param targetTime - The target time to search for in the schedules.
     * @returns The class schedule that matches the target time, or undefined if not found.
     */
    static getClassScheduleByTime(classInstances: ClassInstance[], targetTime: string): ClassInstance | undefined {
        if (classInstances) {
            return classInstances.find(schedule => schedule.start_time === targetTime);
        }
        return undefined;
    }

    /**
  * Retrieves the next class schedule based on the provided class data and optional date.
  * 
  * @param classInstances - The class data containing the class schedules.
  * @param date - Optional date to use for comparison. If not provided, the current date will be used.
  * @returns The next class schedule or, if none found today, the earliest class schedule.
  */
    static nextClassSchedule(classInstances: ClassInstance[], date?: Date): ClassInstance | undefined {
        if (classInstances) {
            if (classInstances.length === 1) return classInstances[0];

            const now = date ? date : new Date();

            // Keep only schedules that actually have a start_time
            const validSchedules = classInstances.filter(s => !!s.start_time);
            if (ObjectUtils.isNullOrEmpty(validSchedules)) return undefined;

            // Filter schedules that are in the future for the current day
            const futureSchedules = validSchedules.filter(schedule => {
                const scheduleDate = DateTimeUtils.hoursToTime(schedule.start_time);
                return scheduleDate > now;
            });

            if (futureSchedules.length > 0) {
                // Find the next class on the current day
                const nextClass = futureSchedules.reduce((prev, current) =>
                    DateTimeUtils.hoursToTime(current.start_time) < DateTimeUtils.hoursToTime(prev.start_time) ? current : prev
                );
                return nextClass;
            }

            // If no future class found today, return the earliest class overall
            const earliestClass = validSchedules.reduce((prev, current) =>
                DateTimeUtils.hoursToTime(current.start_time) < DateTimeUtils.hoursToTime(prev.start_time) ? current : prev
            );

            return earliestClass;
        }
        return undefined;
    }

} 